/**
 * FilePreviewChip - Displays an attached file with preview and status.
 *
 * Shows:
 * - Thumbnail preview for images
 * - Icon for documents
 * - File name (truncated)
 * - File size
 * - Upload progress
 * - Remove button
 * - Error state
 */

import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { FileAttachment } from '../../types/upload';
import { formatFileSize, truncateFilename } from '../../utils/fileValidation';

export interface FilePreviewChipProps {
  attachment: FileAttachment;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

/**
 * Get the appropriate icon for a file type.
 */
function getFileIcon(mimeType: string): React.ReactNode {
  if (mimeType.startsWith('image/')) {
    return <ImageIcon fontSize="small" />;
  }
  if (mimeType === 'application/pdf') {
    return <PictureAsPdfIcon fontSize="small" />;
  }
  if (mimeType === 'text/markdown') {
    return <ArticleIcon fontSize="small" />;
  }
  return <DescriptionIcon fontSize="small" />;
}

/**
 * Get status icon based on upload state.
 */
function getStatusIcon(attachment: FileAttachment): React.ReactNode {
  switch (attachment.uploadStatus) {
    case 'uploading':
      return (
        <CircularProgress
          size={16}
          variant="determinate"
          value={attachment.uploadProgress}
          sx={{ ml: 0.5 }}
        />
      );
    case 'uploaded':
      return (
        <CheckCircleIcon
          fontSize="small"
          sx={{ ml: 0.5, color: 'success.main' }}
        />
      );
    case 'error':
      return (
        <Tooltip title={attachment.errorMessage || 'Upload failed'}>
          <ErrorIcon fontSize="small" sx={{ ml: 0.5, color: 'error.main' }} />
        </Tooltip>
      );
    default:
      return null;
  }
}

export const FilePreviewChip: React.FC<FilePreviewChipProps> = ({
  attachment,
  onRemove,
  disabled = false,
}) => {
  const isImage = attachment.category === 'image';
  const hasPreview = isImage && attachment.preview;
  const isUploading = attachment.uploadStatus === 'uploading';
  const hasError = attachment.uploadStatus === 'error';

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && !isUploading) {
      onRemove(attachment.id);
    }
  };

  return (
    <Chip
      sx={{
        height: 'auto',
        py: 0.5,
        px: 0.5,
        maxWidth: 200,
        backgroundColor: hasError ? 'error.lighter' : 'grey.100',
        borderColor: hasError ? 'error.main' : 'grey.300',
        borderWidth: 1,
        borderStyle: 'solid',
        '& .MuiChip-label': {
          px: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        },
        '& .MuiChip-deleteIcon': {
          margin: 0,
        },
        opacity: isUploading ? 0.8 : 1,
      }}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Preview or Icon */}
          {hasPreview ? (
            <Box
              component="img"
              src={attachment.preview}
              alt={attachment.name}
              sx={{
                width: 24,
                height: 24,
                objectFit: 'cover',
                borderRadius: 0.5,
              }}
            />
          ) : (
            getFileIcon(attachment.mimeType)
          )}

          {/* File info */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={attachment.name}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 500,
                }}
              >
                {truncateFilename(attachment.name, 20)}
              </Typography>
            </Tooltip>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.65rem' }}
            >
              {formatFileSize(attachment.size)}
            </Typography>
          </Box>

          {/* Status indicator */}
          {getStatusIcon(attachment)}
        </Box>
      }
      onDelete={handleRemove}
      deleteIcon={
        <IconButton
          size="small"
          disabled={disabled || isUploading}
          sx={{
            p: 0.25,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    />
  );
};

export default FilePreviewChip;
